import { usesHostedBilling } from '@/lib/appDistribution';
import SettingsSearchTarget from './SettingsSearchTarget';
import AutoTopUpSettings from './AutoTopUpSettings';
import BusinessCreditSummary from '@/components/Business/BusinessCreditSummary';
import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { StyleSheet, View, ActivityIndicator } from 'react-native';
import { useAuthStore, useModalStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useBillingPalette } from './billing/BillingGroup';
import useResponsiveness from '@/hooks/useResponsiveness';
import { trackEvent } from '@/constants/utils/analytics';
import { useTranslation } from '@/hooks/useTranslation';
import { formatCreditBalance, wholeCredits } from '@/lib/formatCredits';
import { availableCredits } from '@/lib/availableCredits';
import { formatCreditResetSchedule } from '@/lib/creditResetDate';

const createStyles = (palette, compact) => StyleSheet.create({
  container: { gap: 24 },
  loadingContainer: { minHeight: 220, justifyContent: 'center', alignItems: 'center' },
  balanceCard: { borderWidth: 1, borderColor: palette.groupBorder, borderRadius: 20, overflow: 'hidden' },
  overview: {
    padding: compact ? 22 : 30,
    flexDirection: compact ? 'column' : 'row',
    alignItems: compact ? 'stretch' : 'center',
    justifyContent: 'space-between',
    gap: 24,
  },
  balance: { flexShrink: 1, gap: 6 },
  // The balance is the page's focal point; larger than a section heading.
  balanceValue: { fontSize: compact ? 40 : 48, lineHeight: compact ? 48 : 56, letterSpacing: -1.5, fontVariant: ['tabular-nums'], color: palette.ink },
  buyButton: { minHeight: 44, paddingHorizontal: 22, borderRadius: 12, backgroundColor: palette.ink },
  breakdown: { flexDirection: compact ? 'column' : 'row', backgroundColor: palette.groupFill, borderTopWidth: 1, borderColor: palette.groupBorder },
  creditDetails: { flex: 1, minWidth: 0, padding: compact ? 22 : 26, gap: 14 },
  divider: { backgroundColor: palette.separator, ...(compact ? { height: 1, marginHorizontal: 22 } : { width: 1, marginVertical: 24 }) },
  label: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  amount: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'baseline', gap: 6 },
  number: { fontVariant: ['tabular-nums'], letterSpacing: -0.5 },
  progressTrack: { height: 4, backgroundColor: palette.tileFill, borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%', backgroundColor: palette.ink, borderRadius: 4 },
  detailCopy: { gap: 7 },
  error: { gap: 12 },
});

function CreditsSettings() {
  const palette = useBillingPalette();
  const { isXS } = useResponsiveness();
  const [width, setWidth] = useState(0);
  const compact = width ? width < 560 : isXS;
  const styles = useMemo(() => createStyles(palette, compact), [palette, compact]);
  const { openModal } = useModalStore(state => state.actions);
  const { t } = useTranslation();
  const userCredits = useAuthStore(state => state.user?.credits);
  const fetchUserCredits = useAuthStore(state => state.fetchUserCredits);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadCredits = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try { if (!await fetchUserCredits({ force: true })) setLoadError(true); }
    catch { setLoadError(true); }
    finally { setLoading(false); }
  }, [fetchUserCredits]);
  useEffect(() => { loadCredits(); }, [loadCredits]);

  useEffect(() => {
    trackEvent('conversion', 'credits_settings_viewed', isPlus ? 'plus' : 'free');
  }, []);

  const handleOpenCreditsPurchaseModal = useCallback(() => {
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [openModal]);

  if (!userCredits && loadError) return <View style={[styles.error, { paddingVertical: 24 }]}>
    <Text accessibilityRole="alert" color="danger">{t('settings.usage.balanceFailed')}</Text>
    <Button text={t('common.retry')} onPress={loadCredits} isLoading={loading} />
  </View>;

  if (!userCredits) return <View style={styles.loadingContainer}>
    <ActivityIndicator size="large" color={palette.ink} accessibilityLabel="Loading credit balance" />
  </View>;

  if (userCredits.business) return <View style={styles.balanceCard}>
    <BusinessCreditSummary credits={userCredits} />
  </View>;

  const monthly = userCredits.monthlyAllotment;
  const hasMonthly = !!(isPlus && monthly);
  const remaining = wholeCredits(monthly?.remaining);
  const total = wholeCredits(monthly?.total);
  const purchased = wholeCredits(userCredits.purchased?.available);
  // Prefer the server's authoritative balance over a client-side calculation.
  const available = availableCredits(userCredits) ?? ((hasMonthly ? remaining : 0) + purchased);
  const remainingPercent = total > 0 ? Math.min(100, remaining / total * 100) : 0;
  const remainingLabel = `${formatCreditBalance(remaining)} of ${formatCreditBalance(total)} credits remaining`;
  const resetSchedule = formatCreditResetSchedule(monthly?.resetDate);
  const resetLabel = resetSchedule
    ? `${t('settings.credits.resets')} ${resetSchedule}`
    : null;

  return (
    <View style={styles.container} onLayout={event => setWidth(event.nativeEvent.layout.width)}>
      {loadError && <View style={styles.error}>
        <Text accessibilityRole="alert" color="danger">{t('settings.usage.balanceFailed')}</Text>
        <Button text={t('common.retry')} onPress={loadCredits} isLoading={loading} />
      </View>}
      <View style={styles.balanceCard}>
        <View style={styles.overview}>
          <View style={styles.balance}>
            <Text size="s" color="secondary" weight="medium">Available credits</Text>
            <Text weight="bold" style={styles.balanceValue} testID="available-credit-balance">{formatCreditBalance(available)}</Text>
            <Text size="s" color="secondary">One balance for everything you create.</Text>
            {userCredits.reservedCredits > 0 && <Text size="s" color="secondary">
              {formatCreditBalance(userCredits.reservedCredits)} credits pending. Running requests reserve credits before they finish. Only actual usage is charged; unused reservations return to your available balance.
            </Text>}
          </View>
          <Button text={t('settings.credits.buyCredits')} onPress={handleOpenCreditsPurchaseModal}
            variant="transparent" style={styles.buyButton} textStyle={{ color: palette.inkInverse }} isFullWidth={compact} />
        </View>
        <View style={styles.breakdown}>
          {hasMonthly && <>
            <View style={styles.creditDetails}>
              <View style={styles.label}>
                <Icon name="sparkles" size={14} color={palette.ink} />
                <Text size="s" weight="medium">{t('settings.credits.monthly')}</Text>
              </View>
              <View style={styles.amount}>
                <Text size="xxl" weight="bold" style={styles.number}>{formatCreditBalance(remaining)}</Text>
                <Text size="xs" color="secondary">of {formatCreditBalance(total)} remaining</Text>
              </View>
              <View style={styles.progressTrack} accessibilityRole="progressbar" accessibilityLabel="Monthly credits remaining"
                accessibilityValue={{ min: 0, max: total, now: Math.min(remaining, total), text: remainingLabel }}
                aria-valuemin={0} aria-valuemax={total} aria-valuenow={Math.min(remaining, total)} aria-valuetext={remainingLabel}>
                <View style={[styles.progressFill, { width: `${remainingPercent}%` }]} />
              </View>
              <View style={styles.detailCopy}>
                {resetLabel && <Text size="xs" color="secondary">{resetLabel}</Text>}
                {remaining === 0 && <Text size="xs" color="secondary">{purchased > 0 ? 'Your purchased credits are ready to use.' : 'Add credits or wait for your next reset.'}</Text>}
              </View>
            </View>
            <View style={styles.divider} />
          </>}
          <View style={styles.creditDetails}>
            <View style={styles.label}>
              <Icon name="coins" size={14} color={palette.ink} />
              <Text size="s" weight="medium">{t('settings.credits.purchased')}</Text>
            </View>
            <View style={styles.amount}>
              <Text size="xxl" weight="bold" style={styles.number}>{formatCreditBalance(purchased)}</Text>
              <Text size="xs" color="secondary">remaining</Text>
            </View>
            <View style={styles.detailCopy}>
              <Text size="xs" color="secondary">{t('settings.credits.neverExpires')}</Text>
              <Text size="xs" color="secondary">Purchased credits stay with you until you use them.</Text>
            </View>
          </View>
        </View>
      </View>
      {usesHostedBilling() && <SettingsSearchTarget label={t('settings.credits.autoTopUp')}><AutoTopUpSettings inCard compact={compact} /></SettingsSearchTarget>}
    </View>
  );
}

export default React.memo(CreditsSettings);
